import React, { type ReactNode } from "react";
import { AspectRatio, Box, Text } from "@chakra-ui/react";
import { Section } from "components/section";
import { BookDemoButton } from "components/event-tracking";
import { FillImage } from "components/images";
import { useWarehouseCdp } from "contexts/warehouse-cdp-context";
import { TextTitle, type TextTitleProps } from "ui/text";
import { horizontalContainerPadding } from "ui/container";
import { type ThemeBgColor, type Theme } from "contexts/color-theme";

const baseImageRatio = 1651 / 806;

type WarehouseHeroProps = {
  theme?: Theme;
  title: string;
  titleMaxW?: TextTitleProps["maxW"];
  description: ReactNode;
};

export function WarehouseHero({
  theme = "light",
  titleMaxW = { base: "2xl", xl: "50%" },
  title,
  description,
}: WarehouseHeroProps) {
  const { warehouse } = useWarehouseCdp();
  const imageAlt = `Flow between the the ${warehouse.name} data warehouse, Hightouch as a Composable CDP, and user's integration ecosystem.`;

  const bgGradient =
    theme === "light"
      ? `linear(to-b, new.${warehouse.color}.150, new.${warehouse.color}.50)`
      : `linear(to-b, new.${warehouse.color}.900, new.${warehouse.color}.700)`;
  const themeBgColor: ThemeBgColor = theme === "light" ? `new.${warehouse.color}.150` : `new.${warehouse.color}.900`;

  return (
    <Section
      noContainerPx
      bgGradient={bgGradient}
      theme={theme}
      themeBgColor={themeBgColor}
      pos="relative"
      overflow="hidden"
      my={0}
      pt={{ base: "bigger", md: "biggest", xl: "hugest" }}
      pb={{ base: "0", xl: "hugest" }}
    >
      <AspectRatio
        display={{ base: "none", xl: "block" }}
        ratio={baseImageRatio}
        pos="absolute"
        bottom={0}
        left={{ base: "30%", "2xl": "32%" }}
        w="100%"
        maxW="1480px"
      >
        <Box pos="relative" boxSize="100%">
          <FillImage
            priority
            src={warehouse.heroImage}
            alt={imageAlt}
            placeholder="empty"
            style={{ objectFit: "cover", objectPosition: "bottom left" }}
          />
        </Box>
      </AspectRatio>

      <Box pos="relative" zIndex={1}>
        <TextTitle
          theme={theme}
          size="xl"
          titleAs="h1"
          descriptionAs="h2"
          title={title}
          description={
            <Text as="span" color={theme === "dark" ? "brand.white" : undefined}>
              {description}
            </Text>
          }
          actions={<BookDemoButton />}
          maxW={titleMaxW}
          mx={{ base: "auto", xl: "0" }}
          px={horizontalContainerPadding}
        />
      </Box>

      <AspectRatio
        display={{ base: "block", xl: "none" }}
        ratio={{ base: 1 / 1, sm: 5 / 4, md: 3 / 2, lg: baseImageRatio }}
        w="100%"
        pos="relative"
        mt={{ base: "-20px", sm: "-40px", md: "-80px", lg: "-120px" }}
      >
        <FillImage
          priority
          src={warehouse.heroImage}
          alt={imageAlt}
          placeholder="empty"
          style={{ objectFit: "cover" }}
        />
      </AspectRatio>
    </Section>
  );
}
